import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { useGetMessage } from 'common/translate';
import { LINE_ITEM_PRICE_MODEL } from 'common/enums/lineItemPriceModel';

import { ToggleControl } from 'components/Controls';

import { CELL_TYPES } from '../../enums';
import { useLineItemField, useLineItemSelector } from '../../context';
import Cell from '../Cell';

import Pricing from './pricing';
import styles from './line.module.css';

const Line = ({ index, disabledState, lockedState }) => {
  const getFlagValue = useLineItemField('getFlagValue');

  const uuid = useLineItemField('uuid');
  const lineItemName = useLineItemField('lineItemName');
  const partnerProductName = useLineItemField('partnerProductName');
  const description = useLineItemField('description');
  const lineItemNameTitle = lineItemName + ' ' + description;

  const quantityType = useLineItemSelector(({ cellTypes }) => cellTypes['quantity']);

  const isPriceModelRecurring = useLineItemSelector(
    ({ priceModelSelected }) => priceModelSelected === LINE_ITEM_PRICE_MODEL.RECURRING
  );
  const priceModelSelectedEditable = useLineItemField('priceModelSelectedEditable');
  const onPriceModelToggle = useLineItemField('onPriceModelToggle');

  const removable = useLineItemField('removable');
  const onRemove = useLineItemField('onRemove');

  const hasError = useLineItemField('hasError');

  const hasNegativeQuantityChild = useLineItemField('hasNegativeQuantityChild');
  // TODO move this logic to model?
  const notes = hasNegativeQuantityChild ? getFlagValue('stopIfHasChildWithNegativeCount') : null;

  const hideCount = getFlagValue('hideCount') === 'TRUE';

  const isDiscountDisabled = useLineItemField('isDiscountDisabled');
  const isRecurringDiscountDisabled = useLineItemField('isRecurringDiscountDisabled');

  const getMessage = useGetMessage();
  const deleteButtonTitle = [
    getMessage('msg_delete'),
    partnerProductName || lineItemName,
    getMessage('msg_line_item'),
  ].join(' ');

  return [
    <tr key={uuid}>
      <Cell
        type={CELL_TYPES.label}
        alias="lineItemName"
        className={cn({ [styles.hasError]: hasError })}
        title={lineItemNameTitle}
      />
      <Cell
        type={hideCount ? CELL_TYPES.null : lockedState ? CELL_TYPES.label : quantityType}
        id={'quantity-' + index}
        alias="quantity"
        className={styles.quantity}
        disabled={disabledState}
        locked={lockedState}
      />
      <Pricing
        id={'discount-' + index}
        disabled={isPriceModelRecurring || disabledState}
        discountDisabled={isDiscountDisabled || disabledState}
        msrp="dealerNet"
        discount="discount"
        percentage="upfrontDiscountPercentage"
        adjusted="upfrontAdjusted"
        extended="upfrontExtended"
        locked={lockedState}
      />
      <td className={styles.priceModel}>
        <ToggleControl
          className={styles.toggle}
          id={`line-item-toggle-${uuid}`}
          msgOn={priceModelSelectedEditable ? 'msg_rent' : 'msg_recurring_cost_short'}
          msgOff="msg_buy"
          value={isPriceModelRecurring}
          disabledState={!priceModelSelectedEditable || disabledState}
          lockedState={lockedState}
          onChange={onPriceModelToggle}
        />
      </td>
      <Pricing
        id={'recurringDiscount-' + index}
        disabled={!isPriceModelRecurring || disabledState}
        discountDisabled={isRecurringDiscountDisabled || disabledState}
        msrp="recurringPrice"
        discount="recurringDiscount"
        percentage="recurringDiscountPercentage"
        adjusted="recurringAdjusted"
        extended="recurringExtended"
        locked={lockedState}
      />
      <td>
        {removable && !lockedState && (
          <button
            id={'line-item_delete-button_' + uuid}
            className={styles.delete}
            onClick={onRemove}
            disabled={disabledState}
            title={deleteButtonTitle}
            aria-label={deleteButtonTitle}
          />
        )}
      </td>
    </tr>,
    notes && (
      <tr key="notes">
        <td colSpan={100} className={cn(styles.notes, 'error')}>
          {notes}
        </td>
      </tr>
    ),
  ];
};

Line.propTypes = {
  index: PropTypes.number.isRequired,
  lockedState: PropTypes.bool,
};

export default Line;
